import { useEffect, useRef, useState } from "react"
import { MyDispatch, MySelector } from "../../redux/store"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import {
  getAllFiles,
  getEventFolders,
  getPhysicalAlbums,
} from "../../redux/gallerySlice"
import { useBrandLogo } from "../../hooks/useBrandLogo"
import logoutIcon from "../../assets/navbar/heroicons-outline_logout.svg"
import whiteLogoutIcon from "../../assets/navbar/logout_white.svg"
import blackLogoutIcon from "../../assets/navbar/logout_black.svg"
import hamburgerIcon from "../../assets/navbar/hamburger.svg"
import hamburgerIconDark from "../../assets/navbar/hamburger_black.svg"
import closeIcon from "../../assets/photo/close.svg"
import closeIconDark from "../../assets/photo/close_black.svg"
import {
  setCurrentFolderData,
  setFolderType,
  setLogout,
  setNavHeight,
  setSelectionMode,
  setSwitchRoleModal,
} from "../../redux/utilSlice"
import { parseGalleryTheme, getDesignLabelClass } from "../../gallery-designs/registry"
import DownloadAllModal from "../modals/DownloadAllModal"
import { safeSessionStorage } from "../../utils/safeStorage"

const Navbar = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch<MyDispatch>()
  const [showDownloadAll, setShowDownloadAll] = useState(false)
  const isDesktop = typeof window !== "undefined" && window.innerWidth > 1024
  const { eventId, category, verified, config, userId, data } = MySelector(
    (state) => state.event
  )

  const { selectedPhotos, fullScreen, navHeight, selectionMode } = MySelector(
    (state) => state.util
  )

  const { theme } = MySelector((state) => state.theme)

  const brandLogo = useBrandLogo()

  const navRef = useRef<HTMLDivElement | null>(null)

  const [showNav, setShowNav] = useState(false)
  const [activeTab, setActiveTab] = useState("gallery")
  const [navOpen, setNavOpen] = useState<boolean>(false)
  // On the gallery route the header stays hidden over the design's cover/hero
  // and only reveals once the user scrolls down toward the photos.
  const [scrolled, setScrolled] = useState(false)

  const changeTab = (route: string) => {
    setActiveTab(route)
    navigate(`/${eventId}/${route}`)
  }

  // to get user details
  // useEffect(() => {
  //   if (!number || !verified) return
  //   dispatch(getUserDetails(number))
  // }, [number, verified, dispatch])

  // to get event folders and all files
  // Fetch even when auth is pending so the gallery renders behind the auth
  // modal (the modal overlay still blocks interaction until verified).
  useEffect(() => {
    if (!eventId || !config) return

    dispatch(getEventFolders(eventId))
    dispatch(getAllFiles({ eventId: eventId || "" }))
  }, [eventId, config, dispatch])

  // to get physical albums
  useEffect(() => {
    if (category !== "host") return
    if (!eventId || !config) return
    if (config?.dataCollectionEnabled && !verified) return

    dispatch(
      getPhysicalAlbums({ eventId: eventId || "", userId: userId || undefined })
    )
  }, [category, eventId, userId, verified, config, dispatch])

  // to hide or show the nav and to set active tab
  useEffect(() => {
    if (!location || !eventId) return
    const urlPaths = location.pathname.split("/").slice(1)
    if (urlPaths[0] === eventId) {
      if (urlPaths[1] === "auth") setShowNav(false)
      else {
        setShowNav(true)
        setActiveTab(urlPaths[1])
      }
    }
  }, [location, eventId])

  // to set the height of the navbar
  useEffect(() => {
    const updateNavHeight = () => {
      if (navRef.current) dispatch(setNavHeight(navRef.current.offsetHeight))
    }

    setTimeout(updateNavHeight, 100)

    const observer = new MutationObserver(() => updateNavHeight())
    if (navRef.current)
      observer.observe(navRef.current, {
        attributes: true,
        childList: true,
        subtree: true,
      })

    const handleResize = () => updateNavHeight()
    window.addEventListener("resize", handleResize)

    return () => {
      observer.disconnect()
      window.removeEventListener("resize", handleResize)
    }
  }, [dispatch])

  // Reveal-on-scroll only applies to custom gallery designs, whose cover/hero
  // the static header would otherwise cover. The default design is unchanged.
  const onGallery = location.pathname.endsWith("/gallery")
  const isCustomDesign = parseGalleryTheme(data?.galleryTheme).designKey !== "default"
  // Reveal-on-scroll wherever the gallery opens with a hero/cover: every custom
  // design, and the default theme when the event has a cover photo.
  const useReveal = onGallery && (isCustomDesign || !!data?.coverPhotoLink)
  // Label casing follows the design on every route (not just the gallery), so
  // the header stays consistent when navigating to About/Photographer/etc.
  const labelClass = isCustomDesign ? getDesignLabelClass(data?.galleryTheme) : ""
  // Nav tab weight: Inter (default theme) reads heavy at 500, so keep it normal
  // there; custom designs keep the medium weight for their uppercase labels.
  const navLinkWeight = isCustomDesign ? "font-[500]" : "font-normal"
  // Default theme's Inter also reads large in the nav; shrink it a touch.
  // Custom designs keep their own label sizing (via labelClass / text-sm).
  const navLinkSize = isCustomDesign ? "" : "text-[13px]"
  const navBtnSize = isCustomDesign ? "text-sm" : "text-[13px]"

  useEffect(() => {
    if (!useReveal) {
      setScrolled(false)
      return
    }
    const container = document.querySelector(
      ".max-h-screen.overflow-y-scroll"
    ) as HTMLElement | null
    if (!container) return
    const handleScroll = () => {
      setScrolled(container.scrollTop > window.innerHeight * 0.1)
    }
    handleScroll()
    container.addEventListener("scroll", handleScroll, { passive: true })
    return () => container.removeEventListener("scroll", handleScroll)
  }, [useReveal, location.pathname])

  const baseVisible =
    showNav && !fullScreen && selectedPhotos.length === 0 && !selectionMode
  // Hold the header off-screen until scrolled past the hero (custom designs only).
  const revealed = !useReveal || scrolled

  return (
    <>
      <div
        ref={navRef}
        className={`${baseVisible ? "flex" : "hidden"} justify-between items-center p-[14px] xl:py-[10px] lg:p-[16px] ${
          useReveal ? "fixed" : "fixed lg:sticky"
        } left-0 right-0 top-0 bg-white/85 dark:bg-black/85 z-[5050] max-h-[63.45px] lg:max-h-[73px] transition-transform duration-300 ease-out ${
          revealed ? "translate-y-0" : "-translate-y-full"
        } ${labelClass}`}
        style={{
          backdropFilter: "blur(12px)",
        }}
      >
        <img
          src={theme === "dark" ? brandLogo.dark : brandLogo.light}
          alt="logo"
          className={`${
            brandLogo.isWhitelabel
              ? // A photographer logo can be any aspect ratio, so cap it by
                // height to fit the navbar and by width so it can't crowd the
                // controls; object-contain preserves its proportions.
                "h-8 lg:h-10 w-auto max-w-[150px] lg:max-w-[200px] object-contain"
              : // The IFE logo is a known wide mark — size it by width.
                "w-[140px] md:w-[160px] lg:w-[180px] xl:w-[200px] h-auto"
          } ${brandLogo.shadowClass}`}
        />
        <div className="flex items-center gap-3">
          {/* Mobile Selection Toggle */}
          {activeTab === "gallery" && (
            <button
              className={`lg:hidden flex items-center justify-center rounded-xl py-1 px-3 ${navLinkWeight} ${labelClass || "text-xs"} ${
                selectionMode
                  ? "bg-golden text-white"
                  : "border border-newOffBlack dark:border-white text-newOffBlack dark:text-white"
              }`}
              onClick={() => {
                dispatch(setSelectionMode(!selectionMode))
              }}
              aria-label="Toggle selection mode"
              title="Toggle selection mode"
            >
              Select
            </button>
          )}
          <span className="lg:hidden">
            {!navOpen ? (
              <img
                onClick={() => setNavOpen(true)}
                src={theme === "dark" ? hamburgerIcon : hamburgerIconDark}
                alt=""
                className="w-6 h-6 md:w-7 md:h-7"
              />
            ) : (
              <img
                onClick={() => setNavOpen(false)}
                src={theme === "dark" ? closeIcon : closeIconDark}
                alt=""
                className="w-6 h-6 md:w-7 md:h-7"
              />
            )}
          </span>
        </div>

        {/* LAPTOP NAVIGATION */}
        <div className="hidden lg:flex items-center gap-[24px]">
          {activeTab === "gallery" && (
            <button
              onClick={() => {
                dispatch(setSelectionMode(!selectionMode))
              }}
              className={`flex items-center justify-center rounded-xl py-1 px-3 ${navLinkWeight} ${labelClass || navBtnSize} ${
                selectionMode
                  ? "bg-golden text-white"
                  : "border border-newOffBlack dark:border-white text-newOffBlack dark:text-white"
              }`}
              aria-label="Toggle selection mode"
              title="Toggle selection mode"
            >
              Select
            </button>
          )}
          <p
            onClick={() => {
              changeTab("gallery")
              dispatch(setFolderType("gallery"))
            }}
            className={`${
              activeTab === "gallery"
                ? "text-newDarkBlack dark:text-golden"
                : "text-newOffBlack dark:text-white"
            } ${navLinkWeight} ${navLinkSize} cursor-pointer transition-all duration-100 ease-linear hover:text-newDarkBlack dark:hover:text-golden`}
          >
            Gallery
          </p>

          {category === "host" && (
            <p
              onClick={() => {
                changeTab("physical-album")
                dispatch(setFolderType("physical"))
                dispatch(setCurrentFolderData([]))
              }}
              className={`${
                activeTab === "physical-album"
                  ? "text-newDarkBlack dark:text-golden"
                  : "text-newOffBlack dark:text-white"
              } ${navLinkWeight} ${navLinkSize} cursor-pointer transition-all duration-100 ease-linear hover:text-newDarkBlack dark:hover:text-golden`}
            >
              Physical Album
            </p>
          )}

          {/* Download All — host on desktop only */}
          {category === "host" && isDesktop && (
            <button
              onClick={() => setShowDownloadAll(true)}
              className={`${navLinkWeight} ${labelClass || navBtnSize} border-[0.5px] border-golden text-golden rounded-xl py-[8px] px-4`}
            >
              Download All
            </button>
          )}

          {/* Switch Role Button */}
          <button
            onClick={() => dispatch(setSwitchRoleModal(true))}
            className={`${navLinkWeight} ${labelClass || navBtnSize} border-[0.5px] border-newOffBlack dark:border-white rounded-xl py-[8px] px-4 text-newOffBlack dark:text-white`}
          >
            {safeSessionStorage.getItem(process.env.REACT_APP_ACTIVE_SESSION || "") === "host" 
              ? "Switch to Guest" 
              : "Switch to Host"}
          </button>

          {config?.dataCollectionEnabled && (
            <button
              onClick={() => dispatch(setLogout(true))}
              className={`${navLinkWeight} ${labelClass || navBtnSize} border-[0.5px] border-newOffBlack dark:border-white rounded-xl py-[8px] px-4 text-newOffBlack dark:text-white flex items-center gap-[8px]`}
            >
              <img
                src={theme === "dark" ? logoutIcon : blackLogoutIcon}
                alt=""
                className="h-5 w-5"
              />
              <span>Log Out</span>
            </button>
          )}
        </div>
      </div>

      {/* MOBILE NAVBAR DROPDOWN */}
      <div
        className={`fixed flex flex-col justify-between w-full items-center overflow-hidden transition-all duration-200 ease-linear ${
          navOpen ? "h-full z-[5050]" : "h-0"
        }`}
        style={{
          top: navHeight,
        }}
      >
        <div
          className="bg-white/85 dark:bg-black/85 w-full py-7"
          style={{
            backdropFilter: "blur(12px)",
          }}
        >
          {navOpen && (
            <div className={`flex flex-col md:text-lg ${labelClass}`}>
              <p
                onClick={() => {
                  changeTab("gallery")
                  dispatch(setFolderType("gallery"))
                  setNavOpen(false)
                }}
                className={`${
                  activeTab === "gallery"
                    ? "text-newDarkBlack dark:text-golden"
                    : "text-newOffBlack dark:text-white"
                } text-center cursor-pointer my-5`}
              >
                Gallery
              </p>
              {category === "host" && (
                <p
                  onClick={() => {
                    changeTab("physical-album")
                    setNavOpen(false)
                  }}
                  className={`${
                    activeTab === "physical-album"
                      ? "text-newDarkBlack dark:text-golden"
                      : "text-newOffBlack dark:text-white"
                  } text-center cursor-pointer my-5`}
                >
                  Physical Album
                </p>
              )}
              <div className="flex justify-center mt-5">
                <div className="w-1/2 h-[1px] border-t border-[#706F6C80] border-dashed" />
              </div>

              {/* Mobile Switch Role Button */}
              <button
                onClick={() => {
                  setNavOpen(false)
                  dispatch(setSwitchRoleModal(true))
                }}
                className={`rounded-xl w-full flex justify-center items-center text-black dark:text-white my-5 ${labelClass}`}
              >
                {safeSessionStorage.getItem(process.env.REACT_APP_ACTIVE_SESSION || "") === "host"
                  ? "Switch to Guest"
                  : "Switch to Host"}
              </button>

              {config?.dataCollectionEnabled && (
                <div className="flex justify-center">
                  <div className="w-1/2 h-[1px] border-t border-[#706F6C80] border-dashed" />
                </div>
              )}
              {config?.dataCollectionEnabled && (
                <button
                  onClick={() => {
                    setNavOpen(false)
                    dispatch(setLogout(true))
                  }}
                  className={`rounded-xl w-full flex justify-center items-center text-black dark:text-white gap-[8px] my-5 ${labelClass}`}
                >
                  <span>Log out</span>{" "}
                  <img
                    src={theme === "dark" ? whiteLogoutIcon : blackLogoutIcon}
                    alt=""
                    className="h-[22px] w-[22px]"
                  />
                </button>
              )}
            </div>
          )}
        </div>
        <div
          className="w-full h-full"
          style={{
            backdropFilter: "blur(2px)",
          }}
          onClick={() => setNavOpen(false)}
        />
      </div>

      <DownloadAllModal open={showDownloadAll} onClose={() => setShowDownloadAll(false)} />
    </>
  )
}

export default Navbar
